import type { ClawerContact } from '../types';
import { getBadgeStyle } from './ui/styles';
import { PeopleNudgeBadge } from './PeopleNudgeBadge';
import type { Nudge } from '../types';

interface ContactListCardProps {
  contact: ClawerContact;
  onClick: () => void;
  nudge?: Nudge | null;
  onSnoozeNudge?: (nudgeId: string) => void;
  onDismissNudge?: (nudgeId: string) => void;
}

// Format relative time (e.g., "2h ago", "3 days ago")
export function formatRelativeTime(timestamp: string | null): string {
  if (!timestamp) return 'No recent activity';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function ContactListCard({
  contact,
  onClick,
  nudge = null,
  onSnoozeNudge,
  onDismissNudge,
}: ContactListCardProps) {
  const displayName = contact.display_name || contact.push_name || contact.phone;
  const relationship = contact.profile?.relationship || null;
  const relationshipStyle = getBadgeStyle(relationship);
  const summary = contact.profile?.summary || 'No conversation summary yet';
  const truncatedSummary = summary.length > 120 ? summary.slice(0, 120) + '...' : summary;

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 mb-3 rounded-xl true-glass border border-edge/50 hover:border-edge/70 hover:shadow-surface transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {/* Top row: Name + Relationship badge */}
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-lg font-bold text-ink flex-1 mr-2 truncate">
          {displayName}
        </h3>
        {nudge ? (
          <PeopleNudgeBadge nudge={nudge} />
        ) : relationship && (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${relationshipStyle.bg} ${relationshipStyle.text}`}
          >
            {relationshipStyle.label}
          </span>
        )}
      </div>

      {/* Middle: Profile summary */}
      <p className="text-sm text-ink-secondary mb-2 leading-relaxed line-clamp-2">
        {truncatedSummary}
      </p>

      {/* Tag chips */}
      {contact.tags && contact.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {contact.tags.map((tag) => (
            <span
              key={tag.id}
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: tag.color + '20',
                color: tag.color,
              }}
            >
              {tag.icon && <span className="mr-0.5">{tag.icon}</span>}
              {tag.label}
            </span>
          ))}
        </div>
      )}

      {nudge && (
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xs text-ink-secondary flex-1 truncate">{nudge.talking_point}</p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSnoozeNudge?.(nudge.id);
            }}
            className="text-xs px-2 py-1 rounded-md bg-canvas border border-edge hover:bg-canvas-secondary"
          >
            Snooze
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismissNudge?.(nudge.id);
            }}
            className="text-xs px-2 py-1 rounded-md bg-white border border-edge hover:bg-canvas"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Bottom row: Timestamp */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-tertiary">
          {formatRelativeTime(contact.last_message_at)}
        </span>
        <span className="text-xs text-ink-tertiary">
          {contact.phone}
        </span>
      </div>
    </button>
  );
}

export default ContactListCard;
